import styles from "./product.module.css";
import { forwardRef, useState, useEffect } from "react";
import { CiHeart } from "react-icons/ci";
import { FaHeart } from "react-icons/fa";
import { LiaRupeeSignSolid } from "react-icons/lia";
import { HeadingTitle, Image } from "../../../shared/index";
import { Link, useNavigate } from "react-router-dom";
import { getImageUrl } from "../../../../lib/get-image";
import toast from "react-hot-toast";

import authService from "../../../../services/authService";

import {
  useAddToWishList,
  useRemoveWishList,
} from "../../../../queries/useWishListQuery";

import {
  useAddToCart,
  useIncreaseCartQty,
  useDecreaseCartQty,
  useGetCart,
} from "../../../../queries/useCartQuery";

const Product = forwardRef(
  ({ product, wishItems, isWishListPending }, ref) => {
    const navigate = useNavigate();

    const { id, name, slug, thumb_image, price, offer_price } = product;

    /* ================= AUTH ================= */

    const [isLoggedIn, setIsLoggedIn] = useState(() =>
      authService.isLoggedIn()
    );

    useEffect(() => {
      const handleAuthChange = () => {
        setIsLoggedIn(authService.isLoggedIn());
      };

      // Login / logout event
      window.addEventListener("auth-change", handleAuthChange);

      // Also check when tab/window gets focus
      window.addEventListener("focus", handleAuthChange);

      return () => {
        window.removeEventListener("auth-change", handleAuthChange);
        window.removeEventListener("focus", handleAuthChange);
      };
    }, []);

    /* ================= CART ================= */

    const { data: cartItems = [] } = useGetCart();

    const { mutate: addToCart, isPending: isAdding } =
      useAddToCart();

    const { mutate: increaseQty } =
      useIncreaseCartQty();

    const { mutate: decreaseQty } =
      useDecreaseCartQty();

    const cartItem = cartItems?.find(
      (item) => item.product_id === id
    );

    const isInCart = Boolean(cartItem);

    /* ================= WISHLIST ================= */

    const { mutate: addWish } = useAddToWishList();
    const { mutate: removeWish } = useRemoveWishList();

    const wishItem = isLoggedIn
      ? wishItems?.find((w) => w.product_id === id)
      : null;

    const inWishList = Boolean(wishItem);
    const wishlistId = wishItem?.id;

    const handleWishlist = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!isLoggedIn) {
        navigate("/login");
        return;
      }

      if (inWishList) {
        removeWish(wishlistId);
      } else {
        addWish({ product_id: id });
      }
    };

    /* ================= PRICE ================= */

    const originalPrice = Number(price) || 0;
    const offerPrice = Number(offer_price) || 0;

    const hasOffer =
      offerPrice > 0 && offerPrice < originalPrice;

    const finalPrice = hasOffer
      ? offerPrice
      : originalPrice;

    const discountPercent = hasOffer
      ? Math.round(
          ((originalPrice - offerPrice) / originalPrice) * 100
        )
      : 0;

    /* ================= QTY ================= */

    const [quantity, setQuantity] = useState(1);

    useEffect(() => {
      setQuantity(1);
    }, [id]);

    /* ================= INCREASE ================= */

    const handleIncrease = (e) => {
      e.stopPropagation();

      if (!isLoggedIn) {
        navigate("/login");
        return;
      }

      setQuantity((prev) => prev + 1);
    };

    /* ================= DECREASE ================= */

    const handleDecrease = (e) => {
      e.stopPropagation();

      if (!isLoggedIn) {
        navigate("/login");
        return;
      }

      setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
    };

    /* ================= ADD TO CART ================= */

    const handleAddToCart = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!isLoggedIn) {
        navigate("/login");
        return;
      }

      if (isInCart) {
        toast.error("Already in cart");
        return;
      }

      addToCart(
        {
          product_id: id,
          quantity: quantity,
        },
        {
          onSuccess: () => {
            toast.success("Item added to cart 🛒");
          },
        }
      );
    };

    return (
      <div ref={ref} className={styles.productCard}>

        {/* ================= IMAGE ================= */}

        <Link to={`/productdetails/${slug}`}>
          <div className={styles.productImageWrapper}>

            <button
              className={styles.wishlistButton}
              onClick={handleWishlist}
              disabled={isWishListPending}
            >
              {inWishList ? (
                <FaHeart
                  size={22}
                  color="#e53935"
                />
              ) : (
                <CiHeart
                  size={22}
                  color="#444"
                />
              )}
            </button>

            <Image
              src={getImageUrl(thumb_image)}
              alt={name}
              className={styles.productImage}
            />
          </div>

          <div className={styles.productInfo}>
            <HeadingTitle
              text={name}
              style={styles.productName}
            />
          </div>
        </Link>

        {/* ================= PRICE ================= */}

        <div className={styles.priceWrapper}>
          <div className={styles.priceBlock}>

            {hasOffer && (
              <span className={styles.discountText}>
                {discountPercent}% OFF
              </span>
            )}

            <div className={styles.priceRow}>
              <p className={styles.productPrice}>
                <LiaRupeeSignSolid />
                {finalPrice}
              </p>

              {hasOffer && (
                <p className={styles.originalPrice}>
                  <LiaRupeeSignSolid />
                  {originalPrice}
                </p>
              )}
            </div>

          </div>
        </div>

        {/* ================= CART ================= */}

        <div className={styles.cartSection}>

          <button
            className={styles.addToCartBtn}
            onClick={handleAddToCart}
            disabled={
              isLoggedIn &&
              (isAdding || isInCart)
            }
          >
            {!isLoggedIn
              ? "Add To Cart 🛒"
              : isInCart
              ? "Add To Cart 🛒"
              : isAdding
              ? "Adding..."
              : "Add To Cart 🛒"}
          </button>

        </div>
      </div>
    );
  }
);

export default Product;